import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { mdiCheck  } from '@mdi/js'
import type { ReactNode } from 'react'
import { SECTION_ICONS } from '../../lib/section-icons'

/**
 * Shared visual primitives for the Data-reliability (Fiabilisation) module.
 *
 * The companion prototype uses a fixed dark-navy palette with hard-coded
 * colours. Per project convention we instead map its semantics onto the
 * platform's CSS-variable tokens so the module follows the light/dark theme:
 *   prototype blue  (#5b7cfa) -> primary
 *   prototype green (#34d399) -> success
 *   prototype amber (#fbbf24) -> warning
 */

export type SourceRef = {
  /** document the value was extracted from, if any */
  documentUid?: string
  documentName?: string
  page?: number | string
  /** manual / saisie label when there is no document */
  manualLabel?: string
  snippet?: string
}

/** A resolvable source chip — document + page link, or a manual-input label. */
export function SourceChip({ org, source }: { org: string; source?: SourceRef }) {
  if (!source) return null
  const label = source.documentName
    || (source.documentUid ? `${source.documentUid.slice(0, 8)}…` : source.manualLabel)
  if (!label) return null

  const inner = (
    <>
      <AppIcon path={SECTION_ICONS.documents} size="xs" className="flex-shrink-0 opacity-80" />
      <span className="truncate">{label}</span>
      {source.page != null && source.page !== '' && (
        <span className="flex-shrink-0">· p.{source.page}</span>
      )}
    </>
  )

  if (source.documentUid) {
    return (
      <Link
        to="/$org/documents/$docUid"
        params={{ org, docUid: source.documentUid }}
        search={source.page != null && source.page !== '' ? { page: source.page } : undefined}
        onClick={(e) => e.stopPropagation()}
        title={source.snippet || label}
        className="inline-flex items-center gap-1 max-w-[220px] truncate rounded-md border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
        data-testid="dr-source-link"
      >
        {inner}
      </Link>
    )
  }

  return (
    <span
      title={source.snippet || label}
      className="inline-flex items-center gap-1 max-w-[220px] truncate rounded-md border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground"
    >
      {inner}
    </span>
  )
}

type Tone = 'primary' | 'success' | 'warning'

const toneClasses: Record<Tone, string> = {
  primary: 'text-primary bg-primary/10',
  success: 'text-success bg-success/10',
  warning: 'text-warning bg-warning/15',
}

/** Small uppercase status pill. */
export function StatusBadge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${toneClasses[tone]}`}>
      {children}
    </span>
  )
}

/** Section title + description block. */
export function SectionHeader({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-lg font-bold text-foreground">{title}</h2>
      <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">{desc}</p>
    </div>
  )
}

/** Green "all good" confirmation strip. */
export function EmptyOK({ label }: { label: string }) {
  return (
    <div
      className="flex items-center gap-2 rounded-xl border border-success/25 bg-success/10 px-4 py-3 text-sm text-foreground"
      data-testid="dr-empty-ok"
    >
      <AppIcon path={mdiCheck} size="md" className="text-success" />
      <span>{label}</span>
    </div>
  )
}

/** Exception corner badge ("!") for lot/equipment cells. */
export function ExceptionDot() {
  return (
    <span className="absolute -right-1.5 -top-1.5 grid h-3.5 w-3.5 place-items-center rounded-full bg-warning text-[10px] font-extrabold text-warning-foreground">
      !
    </span>
  )
}
